/**
 * Workspace do projeto — substitui ProjetoDetalhe.
 *
 * Shell com tabs internas. Cada tab é um componente em ./workspace/.
 * Segue padrão visual do CRM (verde var(--brand, #20a490), fundo claro, cards brancos com sombra).
 * Inspiração de UX: Gestor_Projetos (visão consolidada, tabs ao invés de N páginas separadas).
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  LayoutDashboard, ListTree, Calendar, Clock, Users,
  FileText, UserCheck, Award, History, ArrowLeft, Play, Pause, XCircle, Camera, DollarSign
} from 'lucide-react';
import { projetosAPI } from '../../services/inovacaoAPI';
import { rotuloEstadoProjeto, mensagemErro } from './labels';
import ModalCampos from './ModalCampos';
import MontarProjeto from './MontarProjeto';

import VisaoGeral from './workspace/VisaoGeral';
import Fases from './workspace/Fases';
import AprovacaoPlano from './workspace/AprovacaoPlano';
import Cronograma from './workspace/Cronograma';
import Lancamentos from './workspace/Lancamentos';
import Recursos from './workspace/Recursos';
import AnaliseOrigem from './workspace/AnaliseOrigem';
import AprovadoresWS from './workspace/AprovadoresWS';
import RetrospectivaWS from './workspace/RetrospectivaWS';
import EventosWS from './workspace/EventosWS';
import BaselineWS from './workspace/BaselineWS';
import CustosWS from './workspace/CustosWS';

const COR_ESTADO = {
  planejado: '#95a5a6', em_execucao: '#3498db', em_risco: '#e67e22',
  pausado: '#9b59b6', concluido: '#27ae60', cancelado: '#7f8c8d',
};

const TABS = [
  { id: 'visao', label: 'Visão geral', icon: LayoutDashboard },
  { id: 'fases', label: 'Fases & Entregáveis', icon: ListTree },
  { id: 'aprovacao', label: 'Aprovação do plano', icon: UserCheck },
  { id: 'cronograma', label: 'Cronograma', icon: Calendar },
  { id: 'horas', label: 'Horas', icon: Clock },
  { id: 'custos', label: 'Custos', icon: DollarSign },
  { id: 'recursos', label: 'Recursos', icon: Users },
  { id: 'analise', label: 'Análise de origem', icon: FileText },
  { id: 'aprovadores', label: 'Aprovadores', icon: UserCheck },
  { id: 'baseline', label: 'Plano original', icon: Camera },
  { id: 'retrospectiva', label: 'Retrospectiva', icon: Award },
  { id: 'eventos', label: 'Eventos', icon: History },
];

export default function ProjetoWorkspace() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [fases, setFases] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'visao';
  const setTab = (t) => setSearchParams({ tab: t }, { replace: true });
  const [modalCancelar, setModalCancelar] = useState(false);

  async function recarregar() {
    const [projeto, fs] = await Promise.all([
      projetosAPI.obter(id),
      projetosAPI.listarFases(id),
    ]);
    setP(projeto);
    setFases(fs);
  }

  useEffect(() => {
    recarregar().catch((e) => toast.error(mensagemErro(e, 'Erro ao carregar o projeto')));
  }, [id]);

  async function acao(fn, msg) {
    try { await fn(); toast.success(msg); await recarregar(); }
    catch (e) { toast.error(mensagemErro(e, 'Erro')); }
  }

  if (!p) return <div style={{ padding: 40 }}>Carregando…</div>;

  return (
    <div style={{ background: '#f5f7fa', minHeight: '100vh' }}>
      {/* Header do projeto */}
      <div style={{ background: '#fff', padding: '20px 32px', borderBottom: '1px solid #e1e8ed',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <Link to="/inovacao/projetos"
              style={{ color: '#7f8c8d', textDecoration: 'none', fontSize: '0.85rem',
                       display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ArrowLeft size={14} /> Projetos
        </Link>

        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start', marginTop: 8 }}>
          <div>
            <h1 style={{ margin: 0, color: '#2c3e50' }}>{p.nome}</h1>
            <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{
                background: rotuloEstadoProjeto(p.estado).cor, color: '#fff',
                padding: '3px 12px', borderRadius: 12, fontSize: '0.8rem',
              }}>{rotuloEstadoProjeto(p.estado).label}</span>
              <span style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                📅 {p.data_inicio_planejada} → {p.data_fim_planejada || '?'}
                &nbsp;·&nbsp;🏢 {p.setor_solicitante?.nome}
                &nbsp;·&nbsp;👤 {p.responsavel_tecnico?.nome}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {p.estado === 'em_execucao' && (
              <button onClick={() => acao(() => projetosAPI.pausar(id, 'manual'), 'Pausado')}
                      style={{ ...acaoBtn, background: '#9b59b6' }}>
                <Pause size={14} /> Pausar
              </button>
            )}
            {p.estado === 'pausado' && (
              <button onClick={() => acao(() => projetosAPI.retomar(id), 'Retomado')}
                      style={{ ...acaoBtn, background: '#3498db' }}>
                <Play size={14} /> Retomar
              </button>
            )}
            {!['concluido', 'cancelado'].includes(p.estado) && (
              <button onClick={() => setModalCancelar(true)}
                      style={{ ...acaoBtn, background: '#e74c3c' }}>
                <XCircle size={14} /> Cancelar
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginTop: 20, overflowX: 'auto' }}>
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  padding: '10px 14px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: active ? '3px solid var(--brand, #20a490)' : '3px solid transparent',
                  color: active ? 'var(--brand, #20a490)' : '#7f8c8d',
                  fontWeight: active ? 600 : 500,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap',
                }}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Conteúdo da tab */}
      <div style={{ padding: 32 }}>
        {tab === 'visao' && p.estado === 'planejado' && (
          <MontarProjeto projeto={p} fases={fases} recarregar={recarregar} irParaTab={setTab} />
        )}
        {tab === 'visao' && <VisaoGeral projeto={p} fases={fases} recarregar={recarregar} />}
        {tab === 'fases' && <Fases projeto={p} fases={fases} recarregar={recarregar} />}
        {tab === 'aprovacao' && <AprovacaoPlano projeto={p} recarregar={recarregar} />}
        {tab === 'cronograma' && <Cronograma projeto={p} fases={fases} />}
        {tab === 'horas' && <Lancamentos projeto={p} fases={fases} />}
        {tab === 'custos' && <CustosWS projeto={p} />}
        {tab === 'recursos' && <Recursos projeto={p} fases={fases} />}
        {tab === 'analise' && <AnaliseOrigem projeto={p} />}
        {tab === 'aprovadores' && <AprovadoresWS projeto={p} />}
        {tab === 'baseline' && <BaselineWS projeto={p} />}
        {tab === 'retrospectiva' && <RetrospectivaWS projeto={p} />}
        {tab === 'eventos' && <EventosWS projeto={p} />}
      </div>

      {modalCancelar && (
        <ModalCampos
          titulo="Cancelar projeto"
          descricao={`"${p.nome}" será cancelado. Esta ação não tem volta.`}
          campos={[{ nome: 'motivo', label: 'Motivo do cancelamento', tipo: 'textarea', required: true }]}
          confirmLabel="Confirmar cancelamento"
          confirmCor="#e74c3c"
          onConfirm={(vals) => { setModalCancelar(false); acao(() => projetosAPI.cancelar(id, vals.motivo), 'Projeto cancelado'); }}
          onCancel={() => setModalCancelar(false)}
        />
      )}
    </div>
  );
}

const acaoBtn = {
  color: '#fff', padding: '8px 14px', border: 'none', borderRadius: 6,
  cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: '0.85rem',
  fontWeight: 500,
};
